import { DEVICE_KINDS, DEVICE_PERMISSION_QUERY_NAME, DeviceKind, DevicePermissionSnapshot, emptyDevicePermissions, queryDevicePermission, requestDeviceMedia, screenShareSupported, snapshotFromQueryState, snapshotFromRequestError } from '@src/components/sco/Advisors/utils/devicePermissions';
import { useCallback, useEffect, useState } from 'react';

/**
 * Browser camera / mic / screen-share permission state for the session
 * pre-join card. Query is read-only; `check` is the user-gesture prompt.
 */
export const useDevicePermissions = () => {
  const [devices, setDevices] = useState(emptyDevicePermissions);
  const [checking, setChecking] = useState<DeviceKind | null>(null);
  useEffect(() => {
    if (typeof navigator === 'undefined') {
      return;
    }
    let cancelled = false;
    const unsubscribers: Array<() => void> = [];
    const permissions = navigator.permissions;
    if (!screenShareSupported(navigator.mediaDevices)) {
      setDevices(previous => ({
        ...previous,
        screen: {
          status: 'unavailable',
          detail: 'This browser cannot share your screen.'
        }
      }));
    }
    const load = async () => {
      for (const kind of DEVICE_KINDS) {
        if (!permissions?.query) {
          break;
        }
        try {
          const status = await permissions.query({
            name: DEVICE_PERMISSION_QUERY_NAME[kind]
          } as PermissionDescriptor);
          if (cancelled) {
            return;
          }
          if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
            setDevices(previous_0 => ({
              ...previous_0,
              [kind]: snapshotFromQueryState(status.state)
            }));
          }
          const onChange = () => {
            if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
              setDevices(previous_1 => ({
                ...previous_1,
                [kind]: snapshotFromQueryState(status.state)
              }));
            }
          };
          status.addEventListener('change', onChange);
          unsubscribers.push(() => status.removeEventListener('change', onChange));
        } catch {
          // This browser does not expose this permission name (Safari, display-capture).
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      unsubscribers.forEach(unsubscribe => {
        unsubscribe();
      });
    };
  }, []);
  const check = useCallback(async (kind_0: DeviceKind) => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      setDevices(previous_2 => ({
        ...previous_2,
        [kind_0]: {
          status: 'error',
          detail: 'Device access is not available here.'
        }
      }));
      return;
    }
    setChecking(kind_0);
    setDevices(previous_3 => ({
      ...previous_3,
      [kind_0]: {
        status: 'checking'
      }
    }));
    try {
      await requestDeviceMedia(kind_0, navigator.mediaDevices);
      setDevices(previous_5 => ({
        ...previous_5,
        [kind_0]: {
          status: 'granted'
        }
      }));
    } catch (error) {
      const queried = await queryDevicePermission(kind_0, navigator.permissions);
      setDevices(previous_4 => ({
        ...previous_4,
        [kind_0]: snapshotFromRequestError(error, kind_0, queried)
      }));
    } finally {
      setChecking(current => current === kind_0 ? null : current);
    }
  }, []);
  return {
    devices,
    checking,
    check
  };
};
export type DevicePermissionsState = {
  devices: Record<DeviceKind, DevicePermissionSnapshot>;
  checking: DeviceKind | null;
  check: (kind: DeviceKind) => Promise<void>;
};